/**
 * documentoAlfanumerico.js
 * Fonte única de verdade para CPF / CNPJ (incluindo CNPJ alfanumérico) no React.
 *
 * Regra geral (ida e volta):
 *   IDA   -> formatarDocumento: valor cru (API/estado) vira valor COM máscara p/ exibir.
 *   VOLTA -> limparDocumento: valor da tela vira valor CRU p/ enviar à API / comparar / copiar.
 *            Preserva letras do CNPJ alfanumérico (NUNCA usar replace(/\D/g,''), isso apaga letra).
 *
 * Validação:
 *   validaCpf / validaCnpj / validaDocumento usam o DV oficial.
 *   No CNPJ alfanumérico cada caractere das 12 primeiras posições vale (ASCII - 48);
 *   os 2 dígitos verificadores continuam numéricos. Para CNPJ numérico o resultado
 *   é idêntico ao cálculo clássico (retrocompatível).
 */

import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const CNPJ_CARACTERE_MASCARA = /[A-Za-z0-9]/;

const CNPJ_MASCARA_COMPLETA = [
  CNPJ_CARACTERE_MASCARA,
  CNPJ_CARACTERE_MASCARA,
  '.',
  CNPJ_CARACTERE_MASCARA,
  CNPJ_CARACTERE_MASCARA,
  CNPJ_CARACTERE_MASCARA,
  '.',
  CNPJ_CARACTERE_MASCARA,
  CNPJ_CARACTERE_MASCARA,
  CNPJ_CARACTERE_MASCARA,
  '/',
  CNPJ_CARACTERE_MASCARA,
  CNPJ_CARACTERE_MASCARA,
  CNPJ_CARACTERE_MASCARA,
  CNPJ_CARACTERE_MASCARA,
  '-',
  /\d/,
  /\d/
];

const CPF_MASCARA_COMPLETA = [
  /\d/,
  /\d/,
  /\d/,
  '.',
  /\d/,
  /\d/,
  /\d/,
  '.',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/
];

// Remove tudo que não for letra ou dígito (máscara e espaços inclusos), maiúsculo
export function normalizarDocumentoAlfanumerico(documento) {
  return String(documento || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

// Remove somente os caracteres de máscara (. - / espaço _), preserva letras e dígitos
export function normalizarDocumentoMascara(documento) {
  return String(documento || '')
    .toUpperCase()
    .replace(/[.\-/\s_]/g, '');
}

// 'cpf' ou 'cnpj' a partir do valor (com ou sem máscara)
export function identificarTipoDocumento(documento) {
  const normalizado = normalizarDocumentoAlfanumerico(documento);

  if (/[A-Z]/.test(normalizado)) {
    return 'cnpj'; // qualquer letra já indica CNPJ alfanumérico
  }

  if (normalizado.replace(/\D/g, '').length > 11) {
    return 'cnpj'; // mais de 11 dígitos só cabe em CNPJ
  }

  return 'cpf';
}

// ---------------------------------------------------------------------------
// VOLTA — limpa para persistência / envio à API / comparação / cópia
// ---------------------------------------------------------------------------

export function limparDocumento(documento) {
  return normalizarDocumentoAlfanumerico(documento);
}

// ---------------------------------------------------------------------------
// IDA — formata para exibição (também usado durante a digitação)
// ---------------------------------------------------------------------------

export function formatarDocumento(documento) {
  const normalizado = normalizarDocumentoAlfanumerico(documento);
  const tipo = identificarTipoDocumento(normalizado);
  let resultado = '';
  let i;

  if (tipo === 'cpf') {
    const digitos = normalizado.replace(/\D/g, '').substring(0, 11);
    for (i = 0; i < digitos.length; i++) {
      if (i === 3 || i === 6) resultado += '.';
      else if (i === 9) resultado += '-';
      resultado += digitos.charAt(i);
    }
    return resultado;
  }

  // CNPJ alfanumérico:
  // - 12 primeiros caracteres (raiz + ordem) aceitam letra ou dígito
  // - 2 últimos (dígitos verificadores) aceitam SOMENTE número; letra ali é descartada
  let alfanumerico = '';
  for (i = 0; i < normalizado.length && alfanumerico.length < 14; i++) {
    const caractere = normalizado.charAt(i);
    if (alfanumerico.length < 12 || /[0-9]/.test(caractere)) {
      alfanumerico += caractere;
    }
  }

  for (i = 0; i < alfanumerico.length; i++) {
    if (i === 2 || i === 5) resultado += '.';
    else if (i === 8) resultado += '/';
    else if (i === 12) resultado += '-';
    resultado += alfanumerico.charAt(i);
  }

  return resultado;
}

// Alias para exibição semântica (ex.: mascararCpfCnpj antigo pode delegar aqui)
export const mascararDocumento = formatarDocumento;

export function CPFCNPJMask(value) {
  const normalizado = normalizarDocumentoAlfanumerico(value);
  return identificarTipoDocumento(normalizado) === 'cnpj' ? CNPJ_MASCARA_COMPLETA : CPF_MASCARA_COMPLETA;
}

export function CPFMask(value) {
  const tamanho = normalizarDocumentoAlfanumerico(value).replace(/\D/g, '').length;

  if (tamanho > 3 && tamanho <= 6) {
    return [/\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/];
  } else if (tamanho >= 7 && tamanho <= 9) {
    return [/\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/];
  } else if (tamanho >= 10) {
    return CPF_MASCARA_COMPLETA;
  }

  return [/\d/, /\d/, /\d/];
}

export function CNPJMask(value) {
  const tamanho = normalizarDocumentoAlfanumerico(value).length;

  if (tamanho <= 2) {
    return [CNPJ_CARACTERE_MASCARA, CNPJ_CARACTERE_MASCARA];
  } else if (tamanho > 2 && tamanho <= 5) {
    return [
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      '.',
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA
    ];
  } else if (tamanho > 5 && tamanho <= 8) {
    return [
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      '.',
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      '.',
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA
    ];
  } else if (tamanho > 8 && tamanho <= 12) {
    return [
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      '.',
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      '.',
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      '/',
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA,
      CNPJ_CARACTERE_MASCARA
    ];
  }

  return CNPJ_MASCARA_COMPLETA;
}

// CPF — sempre numérico, 11 dígitos, com DV
export function validaCpf(cpf) {
  const documento = normalizarDocumentoAlfanumerico(cpf).replace(/\D/g, '');

  if (documento.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(documento)) return false; // todos iguais

  let soma = 0;
  for (let i = 0; i < 9; i++) {
    soma += parseInt(documento.charAt(i), 10) * (10 - i);
  }
  let resto = (soma * 10) % 11;
  if (resto === 10) resto = 0;
  if (resto !== parseInt(documento.charAt(9), 10)) return false;

  soma = 0;
  for (let i = 0; i < 10; i++) {
    soma += parseInt(documento.charAt(i), 10) * (11 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10) resto = 0;
  if (resto !== parseInt(documento.charAt(10), 10)) return false;

  return true;
}

// CNPJ — numérico OU alfanumérico, 14 caracteres, com DV oficial
export function validaCnpj(cnpj) {
  const documento = normalizarDocumentoAlfanumerico(cnpj);

  if (documento.length !== 14) return false;
  if (/^(.)\1{13}$/.test(documento)) return false; // todos iguais (000..., AAA..., etc.)
  if (!/^[0-9]{2}$/.test(documento.substring(12))) return false; // DV precisa ser numérico

  const valorCaractere = caractere => caractere.charCodeAt(0) - 48;

  const calculaDigito = tamanho => {
    let soma = 0;
    let pos = tamanho - 7;
    for (let i = tamanho; i >= 1; i--) {
      soma += valorCaractere(documento.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    return soma % 11 < 2 ? 0 : 11 - (soma % 11);
  };

  const digitos = documento.substring(12);

  if (calculaDigito(12) !== parseInt(digitos.charAt(0), 10)) return false;
  if (calculaDigito(13) !== parseInt(digitos.charAt(1), 10)) return false;

  return true;
}

// Valida escolhendo CPF ou CNPJ automaticamente
export function validaDocumento(documento) {
  return identificarTipoDocumento(documento) === 'cpf' ? validaCpf(documento) : validaCnpj(documento);
}

export function validaDocumentoPorTipo(documento, tipoDocumento) {
  const tipo = String(tipoDocumento || '').toLowerCase();
  const tipoIdentificado = identificarTipoDocumento(documento);

  if (tipo === 'pf' || tipo === 'cpf') {
    return tipoIdentificado === 'cpf' && validaCpf(documento);
  }

  if (tipo === 'pj' || tipo === 'cnpj') {
    return tipoIdentificado === 'cnpj' && validaCnpj(documento);
  }

  return validaDocumento(documento);
}

function contarUteisAntesDaPosicao(texto, posicao) {
  let contagem = 0;
  for (let i = 0; i < posicao && i < texto.length; i++) {
    if (/[A-Z0-9]/i.test(texto.charAt(i))) contagem++;
  }
  return contagem;
}

function posicaoAposNUteis(textoMascarado, qtdUteis) {
  if (qtdUteis <= 0) return 0;
  let contagem = 0;
  for (let i = 0; i < textoMascarado.length; i++) {
    if (/[A-Z0-9]/i.test(textoMascarado.charAt(i))) {
      contagem++;
      if (contagem === qtdUteis) return i + 1;
    }
  }
  return textoMascarado.length;
}

export function useDocumentoCpfCnpj(valorInicial = '') {
  const [valor, setValor] = useState(() => formatarDocumento(valorInicial));
  const inputRef = useRef(null);
  const caretRef = useRef(null);

  const handleChange = useCallback(event => {
    const el = event.target;
    const valorBruto = String(el.value || '');
    const cursor = typeof el.selectionStart === 'number' ? el.selectionStart : null;

    const formatado = formatarDocumento(valorBruto);

    if (cursor !== null) {
      const uteisAntes = contarUteisAntesDaPosicao(valorBruto.toUpperCase(), cursor);
      caretRef.current = posicaoAposNUteis(formatado, uteisAntes);
    }

    setValor(formatado);
  }, []);

  useLayoutEffect(() => {
    if (caretRef.current !== null && inputRef.current) {
      const pos = caretRef.current;
      caretRef.current = null;
      try {
        inputRef.current.setSelectionRange(pos, pos);
      } catch (e) {
        /* input pode não suportar setSelectionRange */
      }
    }
  }, [valor]);

  const definirValor = useCallback(novoValor => {
    caretRef.current = null;
    setValor(formatarDocumento(novoValor));
  }, []);

  return {
    valor, // formatado — use no value do TextField
    valorLimpo: limparDocumento(valor), // cru — use ao enviar p/ API
    documentoValido: validaDocumento(valor), // boolean pronto p/ validação
    inputRef, // passe no inputRef do TextField (MUI)
    handleChange, // passe no onChange do TextField
    definirValor // p/ preencher o campo programaticamente (ida)
  };
}

export const formatarCnpjAlfanumerico = formatarDocumento;
export const limparCnpjAlfanumerico = limparDocumento;
